import { RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';
import { Chip } from '../ui';
import { api, ApiError } from '../lib/api';
import { contactCreateBody, contactTags } from './contact-form';
import type { RecursoConfig } from './CrudScreen';
import { FormularioDisparo } from './FormularioDisparo';
import { FormularioAgendamento, paraCampoDeData } from './FormularioAgendamento';

/**
 * Configuração das telas de recurso.
 *
 * É a lista inteira num arquivo só, de propósito: dá para ver de uma vez que
 * todas as tabelas usam as mesmas colunas de metadado, que papel é exigido em
 * cada operação, e onde falta estado vazio. Espalhado em dez componentes, foi
 * exatamente isso que ninguém conseguiu ver.
 */

const dataCurta = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const dataHora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** Status → tom. Fora daqui, cada tela escolhia a cor por conta e o mesmo
 *  `PENDING` aparecia amarelo numa tela e cinza em outra. */
const TOM: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  APPROVED: 'success', READY: 'success', SENT: 'success', ACTIVE: 'success', DELIVERED: 'success',
  PENDING: 'warning', SCHEDULED: 'warning', IN_REVIEW: 'warning', PROCESSING: 'warning',
  REJECTED: 'danger', FAILED: 'danger', CANCELED: 'danger', PAUSED: 'danger',
};

const ROTULO: Record<string, string> = {
  APPROVED: 'Aprovado', PENDING: 'Pendente', REJECTED: 'Reprovado', IN_REVIEW: 'Em análise',
  SCHEDULED: 'Agendado', SENT: 'Enviado', FAILED: 'Falhou', CANCELED: 'Cancelado',
  READY: 'Indexado', PROCESSING: 'Processando', ACTIVE: 'Ativo', PAUSED: 'Pausado',
};

function Status({ valor }: { valor: string }) {
  return <Chip tone={TOM[valor] ?? 'neutral'}>{ROTULO[valor] ?? valor}</Chip>;
}

const PAPEIS = [
  { value: 'AGENT', label: 'Atendente — atende conversas' },
  { value: 'SUPERVISOR', label: 'Supervisor — atende e dispara campanhas' },
  { value: 'ADMIN', label: 'Administrador — configura o workspace' },
] as const;

interface Tag { id: string; name: string; color: string | null; _count?: { contacts: number } }
interface Contato { id:string;waId:string;displayName:string|null;profileName:string|null;crmContactId:string|null;crmMergedIntoId:string|null;crmJobTitle:string|null;crmAccountId:string|null;crmAccountName:string|null;crmOrganizations?:Array<{id:string;name:string;relationshipRole?:string|null}>;crmSyncedAt:string|null;createdAt:string;marketingOptInAt:string|null;tags?:Tag[];duplicateSuggestions?:Array<{contactId:string;name:string|null;waId:string;confidence:'high'|'medium';reasons:string[]}> }
interface Modelo { id: string; name: string; language: string; category: string; status: string; lastSyncedAt: string | null }
interface Campanha { id: string; name: string; tagName: string | null; templateName: string; scheduledAt: string; sent: number; recipients: number; failed: number; status: string }
interface Agendada { id: string; waId: string; profileName: string | null; templateName: string; scheduledAt: string; status: string }
interface Operador {
  id: string; name: string; email: string; role: string;
  availability: string; isActive: boolean; capacity: number;
  _count?: { conversations: number };
}

export const TAGS: RecursoConfig<Tag> = {
  path: '/workspace/tags',
  rotuloPlural: 'etiquetas',
  chave: (t) => t.id,
  rotuloLinha: (t) => t.name,
  colunas: [
    { key: 'nome', header: 'Etiqueta', cell: (t) => <Chip kind="tag" color={t.color}>{t.name}</Chip> },
    { key: 'contatos', header: 'Contatos', cell: (t) => String(t._count?.contacts ?? 0) },
  ],
  vazio: {
    title: 'Nenhuma etiqueta ainda',
    text: 'Etiquetas agrupam contatos para segmentar disparos e filtrar a caixa de entrada.',
  },
  /* A cor é OBRIGATÓRIA no servidor (`z.string().regex(/^#[0-9a-f]{6}$/i)`,
     sem `.optional()`), e este formulário a oferecia como se fosse opcional e
     sem valor inicial. Deixar em branco reprovava a criação inteira com "Nome
     ou cor inválidos" — mensagem que não diz qual dos dois está errado. */
  criar: {
    rotulo: 'Nova etiqueta',
    titulo: 'Criar etiqueta',
    papel: 'AGENT',
    campos: [
      { nome: 'name', rotulo: 'Nome', obrigatorio: true, placeholder: 'Clientes VIP' },
      { nome: 'color', rotulo: 'Cor', tipo: 'text', obrigatorio: true, padrao: '#25d366',
        dica: 'Hexadecimal de 6 dígitos, como #25d366.' },
    ],
  },
  /* Renomear existia no servidor com tratamento de conflito (409, "já existe
     uma etiqueta com esse nome") e não tinha caminho na tela. Sem isso,
     corrigir um erro de digitação exigia apagar a etiqueta — o que a remove de
     todos os contatos — e refazer o vínculo um a um. */
  editar: {
    titulo: 'Editar etiqueta',
    papel: 'AGENT',
    campos: [
      { nome: 'name', rotulo: 'Nome', obrigatorio: true },
      { nome: 'color', rotulo: 'Cor', tipo: 'text', obrigatorio: true, dica: 'Hexadecimal de 6 dígitos, como #25d366.' },
    ],
    valores: (t) => ({ name: t.name, color: t.color ?? '#25d366' }),
    corpo: (v) => ({ name: v.name, color: v.color }),
  },
  excluir: { papel: 'AGENT', aviso: (t) => `A etiqueta "${t.name}" será removida de todos os contatos. Os contatos permanecem.` },
};

export const CONTATOS: RecursoConfig<Contato> = {
  path: '/workspace/contacts',
  rotuloPlural: 'contatos',
  chave: (c) => c.id,
  rotuloLinha: (c) => c.displayName ?? c.profileName ?? c.waId,
  colunas: [
    { key: 'nome', header: 'Contato', cell: (c) => c.displayName ?? c.profileName ?? 'Sem nome' },
    { key: 'numero', header: 'WhatsApp', cell: (c) => c.waId },
    { key: 'duplicidade', header: 'Duplicidade', cell: (c) => c.duplicateSuggestions?.length
        ? <span title={c.duplicateSuggestions.map((item)=>`${item.name ?? 'Sem nome'} · ${item.waId}`).join('\n')}>
            <Chip tone="warning">{`${c.duplicateSuggestions.length} possível${c.duplicateSuggestions.length === 1 ? '' : 'is'}`}</Chip>
          </span>
        : <Chip tone="neutral">Nenhuma</Chip> },
    { key: 'crm', header: 'Contexto CRM', cell: (c) => c.crmMergedIntoId
        ? <span style={{display:'grid',gap:'2px'}}><Chip tone="success">Consolidado no CRM</Chip><small title={c.crmMergedIntoId}>Histórico preservado neste contato</small></span>
        : c.crmContactId
        ? <span style={{display:'grid',gap:'2px'}}><strong>{c.crmOrganizations?.length ? c.crmOrganizations.map((item)=>item.name).join(' · ') : c.crmAccountName ?? 'Sem empresa vinculada'}</strong><small>{c.crmJobTitle ?? 'Cargo não informado'} · sincronizado {c.crmSyncedAt ? dataCurta(c.crmSyncedAt) : 'agora'}</small></span>
        : <Chip tone="neutral">Não sincronizado</Chip> },
    { key: 'etiquetas', header: 'Etiquetas', cell: (c) => contactTags(c.tags).length
        ? <span style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {contactTags(c.tags).map((t) => <Chip key={t.id} kind="tag" color={t.color}>{t.name}</Chip>)}
          </span>
        : '—' },
    /* Consentimento promocional é obrigação regulatória, não enfeite: precisa
       ser visível na lista, não escondido no detalhe. */
    { key: 'optin', header: 'Promocional', cell: (c) => c.marketingOptInAt
        ? <Chip tone="success">Aceito</Chip>
        : <Chip tone="neutral">Não aceito</Chip> },
    { key: 'criado', header: 'Criado em', cell: (c) => dataCurta(c.createdAt) },
  ],
  vazio: {
    title: 'Nenhum contato ainda',
    text: 'Contatos aparecem sozinhos quando alguém escreve para o seu número. Você também pode cadastrar manualmente.',
  },
  criar: {
    rotulo: 'Novo contato',
    titulo: 'Iniciar conversa',
    papel: 'AGENT',
    campos: [
      { nome: 'waId', rotulo: 'WhatsApp', tipo: 'tel', obrigatorio: true, dica: 'Com código do país e DDD, apenas dígitos. O número é uma identidade compartilhada do contato.', placeholder: '5511999998888' },
      { nome: 'displayName', rotulo: 'Nome de exibição', placeholder: 'Maria Silva', dica:'Opcional. O cadastro completo pertence ao Lead Machine.' },
    ],
    /* A tabela usa os nomes do domínio (`waId` e `profileName`), enquanto o
       endpoint de criação recebe o contrato público (`number` e `name`). Sem
       esta tradução o formulário enviava campos válidos com nomes errados e
       o servidor respondia como se número e nome estivessem vazios. */
    corpo: contactCreateBody,
  },
  /**
   * Consentimento promocional.
   *
   * É obrigação regulatória, não preferência: sem opt-in registrado, o contato
   * não pode entrar em disparo de marketing. A coluna já mostrava o estado,
   * mas mudá-lo exigia voltar ao front antigo — a informação estava visível e
   * a ação, não.
   *
   * Fica como ação de linha e não como campo de formulário porque é um
   * `PATCH` de um booleano numa sub-rota, com registro de origem
   * (`MANUAL_ADMIN`) no servidor. Enfiar num formulário de edição sugeriria
   * que é editável junto com nome e telefone, e não é.
   */
  acoesExtras: (c, { recarregar, avisar }) => [{
    rotulo: c.marketingOptInAt ? 'Revogar opt-in promocional' : 'Registrar opt-in promocional',
    icone: c.marketingOptInAt ? <ShieldOff size={16} aria-hidden="true" /> : <ShieldCheck size={16} aria-hidden="true" />,
    onSelect: async () => {
      const conceder = !c.marketingOptInAt;
      try {
        await api(`/workspace/contacts/${c.id}/marketing-consent`, {
          method: 'PATCH',
          body: { granted: conceder },
        });
        recarregar();
        avisar(conceder
          ? `${c.displayName ?? c.profileName ?? c.waId} passa a receber mensagens promocionais.`
          : `${c.displayName ?? c.profileName ?? c.waId} sai dos disparos promocionais.`);
      } catch (erro) {
        avisar(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o consentimento.', 'danger');
      }
    },
  }],
  /* Só o nome. O `waId` é a identidade do contato na Meta — trocá-lo não
     renomeia ninguém, cria outra pessoa — e o servidor nem aceita o campo. */
  editar: {
    titulo: 'Editar contato',
    papel: 'AGENT',
    campos: [
      { nome: 'displayName', rotulo: 'Nome de exibição', dica: 'Em branco, prevalece o nome do perfil do WhatsApp.' },
    ],
    valores: (c) => ({ displayName: c.displayName ?? '' }),
    // String vazia reprovaria no `min(1)`; o schema aceita `null`, que é o
    // jeito de dizer "sem nome" em vez de "nome em branco".
    corpo: (v) => ({ displayName: v.displayName?.trim() ? v.displayName.trim() : null }),
  },
  excluir: { papel: 'SUPERVISOR', aviso: (c) => `O contato ${c.displayName ?? c.profileName ?? c.waId} será removido das listas. O histórico de conversas será preservado.` },
};

export const MODELOS: RecursoConfig<Modelo> = {
  path: '/workspace/templates',
  rotuloPlural: 'modelos',
  chave: (t) => t.id,
  rotuloLinha: (t) => t.name,
  colunas: [
    { key: 'nome', header: 'Modelo', cell: (t) => t.name },
    { key: 'idioma', header: 'Idioma', cell: (t) => t.language },
    { key: 'categoria', header: 'Categoria', cell: (t) => t.category },
    { key: 'status', header: 'Status', cell: (t) => <Status valor={t.status} /> },
    { key: 'sync', header: 'Sincronizado', cell: (t) => dataHora(t.lastSyncedAt) },
  ],
  vazio: {
    title: 'Nenhum modelo sincronizado',
    text: 'Modelos são criados e aprovados na Meta. Sincronize para trazê-los para cá.',
  },
  /* O texto do vazio manda sincronizar desde sempre; o botão não existia. A
     contagem entra no aviso porque "sincronizado" sem número não distingue
     "trouxe 12" de "não havia nada para trazer" — e são situações que pedem
     reações opostas de quem clicou. */
  acaoGlobal: {
    rotulo: 'Sincronizar com a Meta',
    icone: <RefreshCw size={18} aria-hidden="true" />,
    papel: 'ADMIN',
    executar: async () => {
      const r = await api<{ synchronized: number; accounts: number }>('/workspace/templates/sync', { method: 'POST' });
      if (r.accounts === 0) return 'Nenhuma conta WABA conectada para sincronizar.';
      const contas = r.accounts === 1 ? '1 conta' : `${r.accounts} contas`;
      return r.synchronized === 0
        ? `Nenhum modelo encontrado em ${contas}.`
        : `${r.synchronized} ${r.synchronized === 1 ? 'modelo sincronizado' : 'modelos sincronizados'} de ${contas}.`;
    },
  },
};

export const CAMPANHAS: RecursoConfig<Campanha> = {
  path: '/workspace/campaigns',
  rotuloPlural: 'disparos',
  // `LIMIT 100` no servidor, sem paginação. Ver `useResource.teto`.
  teto: 100,
  chave: (c) => c.id,
  rotuloLinha: (c) => c.name,
  colunas: [
    { key: 'nome', header: 'Disparo', cell: (c) => c.name },
    { key: 'segmento', header: 'Segmento', cell: (c) => c.tagName ?? 'Todos os contatos' },
    { key: 'modelo', header: 'Modelo', cell: (c) => c.templateName },
    { key: 'quando', header: 'Agendado para', cell: (c) => dataHora(c.scheduledAt) },
    /* Enviados/total numa célula só: duas colunas separadas obrigavam a
       comparar números distantes para saber se o disparo andou. */
    { key: 'progresso', header: 'Progresso', cell: (c) => `${c.sent} de ${c.recipients}${c.failed ? ` · ${c.failed} falharam` : ''}` },
    { key: 'status', header: 'Status', cell: (c) => <Status valor={c.status} /> },
  ],
  vazio: {
    title: 'Nenhum disparo ainda',
    text: 'Um disparo envia um modelo aprovado para um segmento de contatos que aceitou mensagens promocionais.',
  },
  /* Formulário próprio, e não `criar`: a lista de modelos depende do número
     escolhido e o alcance do segmento precisa ser consultado antes de agendar.
     O motivo está por extenso em `FormularioDisparo`. */
  criarPersonalizado: {
    rotulo: 'Novo disparo',
    papel: 'SUPERVISOR',
    render: (fechar, concluir) => <FormularioDisparo onFechar={fechar} onCriado={concluir} />,
  },
  excluir: { papel: 'SUPERVISOR', aviso: (c) => `O disparo "${c.name}" será cancelado e as mensagens ainda não enviadas não sairão. O que já foi enviado permanece.` },
};

export const AGENDADAS: RecursoConfig<Agendada> = {
  path: '/workspace/scheduled-messages',
  rotuloPlural: 'agendamentos',
  // `LIMIT 200` no servidor, sem paginação. Ver `useResource.teto`.
  teto: 200,
  chave: (s) => s.id,
  /* O nome do contato NÃO basta: o mesmo contato pode ter vários envios
     agendados, e o menu de ações de cada linha ficaria com rótulo idêntico —
     exatamente o problema que o `Kebab` existe para evitar. O horário é o que
     distingue uma linha da outra. Descoberto quando um teste tentou clicar em
     "Ações de Maria Silva" e encontrou dois. */
  rotuloLinha: (s) => `${s.profileName ?? s.waId} em ${dataHora(s.scheduledAt)}`,
  colunas: [
    { key: 'contato', header: 'Contato', cell: (s) => s.profileName ?? s.waId },
    { key: 'modelo', header: 'Modelo', cell: (s) => s.templateName },
    { key: 'quando', header: 'Envio', cell: (s) => dataHora(s.scheduledAt) },
    { key: 'status', header: 'Status', cell: (s) => <Status valor={s.status} /> },
  ],
  vazio: {
    title: 'Nenhum agendamento',
    text: 'Mensagens agendadas aparecem aqui até a hora do envio, e podem ser canceladas antes disso.',
  },
  criarPersonalizado: {
    rotulo: 'Agendar envio',
    papel: 'SUPERVISOR',
    render: (fechar, concluir) => <FormularioAgendamento onFechar={fechar} onCriado={concluir} />,
  },
  /* Reagendar. O `PATCH` faz o ciclo completo — cancela o job, grava,
     re-enfileira — e só aceita PENDENTE; para os demais responde 404. Daí o
     `disponivel`: um "Editar" que sempre falha é pior do que a ausência dele. */
  editar: {
    titulo: 'Reagendar envio',
    papel: 'SUPERVISOR',
    disponivel: (s) => s.status === 'PENDING',
    campos: [{
      nome: 'scheduledAt',
      rotulo: 'Novo horário',
      tipo: 'datetime-local',
      obrigatorio: true,
      dica: 'Pelo menos um minuto no futuro. O envio antigo é cancelado e um novo entra na fila.',
    }],
    valores: (s) => ({ scheduledAt: paraCampoDeData(new Date(s.scheduledAt)) }),
    corpo: (v) => ({ scheduledAt: new Date(v.scheduledAt!).toISOString() }),
  },
  excluir: { papel: 'SUPERVISOR', aviso: (s) => `O envio agendado para ${s.profileName ?? s.waId} será cancelado.` },
};

export const OPERADORES: RecursoConfig<Operador> = {
  path: '/workspace/agents',
  rotuloPlural: 'operadores',
  chave: (a) => a.id,
  rotuloLinha: (a) => a.name,
  verComo: 'ADMIN',
  colunas: [
    { key: 'nome', header: 'Operador', cell: (a) => a.name },
    { key: 'email', header: 'E-mail', cell: (a) => a.email },
    { key: 'papel', header: 'Papel', cell: (a) => ({ ADMIN: 'Administrador', SUPERVISOR: 'Supervisor', AGENT: 'Atendente' }[a.role] ?? a.role) },
    { key: 'fila', header: 'Carga', cell: (a) => `${a._count?.conversations ?? 0} de ${a.capacity}` },
    /* Ativo/inativo estava invisível na lista. Um operador desativado continua
       aparecendo com nome, papel e carga — sem esta coluna, a única forma de
       saber que ele não atende mais era abrir a edição. */
    { key: 'ativo', header: 'Conta', cell: (a) => (
      a.isActive ? <Chip tone="success">Ativa</Chip> : <Chip tone="danger">Desativada</Chip>
    ) },
  ],
  vazio: {
    title: 'Nenhum operador além de você',
    text: 'Operadores atendem conversas. Gestores também disparam campanhas; administradores configuram o workspace.',
  },
  criar: {
    rotulo: 'Novo operador',
    titulo: 'Convidar operador',
    papel: 'ADMIN',
    campos: [
      { nome: 'name', rotulo: 'Nome', obrigatorio: true },
      { nome: 'email', rotulo: 'E-mail', tipo: 'email', obrigatorio: true },
      { nome: 'role', rotulo: 'Papel', tipo: 'select', obrigatorio: true, opcoes: PAPEIS },
    ],
  },
  /**
   * `PATCH /agents/:id` valida o objeto INTEIRO (nome, papel, ativo,
   * disponibilidade, capacidade), não um diff. Mandar só o campo alterado
   * devolve 400 "Dados do operador inválidos" — por isso `corpo` remonta tudo.
   *
   * O servidor ainda recusa dois casos que a interface não tenta esconder,
   * porque a explicação só existe lá: desativar a própria conta, e remover o
   * último administrador ativo. A mensagem dele aparece dentro do diálogo.
   */
  editar: {
    titulo: 'Editar operador',
    papel: 'ADMIN',
    campos: [
      { nome: 'name', rotulo: 'Nome', obrigatorio: true },
      { nome: 'role', rotulo: 'Papel', tipo: 'select', obrigatorio: true, opcoes: PAPEIS },
      { nome: 'capacity', rotulo: 'Capacidade', tipo: 'number', obrigatorio: true,
        dica: 'Quantas conversas simultâneas a distribuição automática pode atribuir.' },
      { nome: 'availability', rotulo: 'Disponibilidade', tipo: 'select', obrigatorio: true, opcoes: [
        { value: 'AVAILABLE', label: 'Disponível' },
        { value: 'BUSY', label: 'Ocupado' },
        { value: 'OFFLINE', label: 'Fora' },
      ] },
      { nome: 'isActive', rotulo: 'Conta ativa (pode entrar e atender)', tipo: 'checkbox' },
      { nome: 'password', rotulo: 'Nova senha', tipo: 'password',
        dica: 'Deixe em branco para manter a atual. Mínimo de 10 caracteres; trocar encerra as sessões abertas.' },
    ],
    valores: (a) => ({
      name: a.name,
      role: a.role,
      capacity: String(a.capacity ?? 10),
      availability: a.availability,
      isActive: a.isActive,
    }),
    corpo: (v) => ({
      name: v.name,
      role: v.role,
      capacity: Number(v.capacity),
      availability: v.availability,
      isActive: v.isActive === 'true',
      // Campo vazio significa "não trocar". Enviar string vazia reprovaria no
      // mínimo de 10 caracteres e bloquearia qualquer outra alteração.
      ...(v.password ? { password: v.password } : {}),
    }),
  },
};
